import { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COLORS = ["#2563eb", "#f97316", "#10b981", "#a855f7", "#ef4444", "#facc15"];

function formatNumber(n) {
  if (n === null || n === undefined || isNaN(n)) return "";
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return n.toFixed(0);
}

// Convert the canonical payload into Recharts-friendly row data:
//   labels: ["A","B"], datasets: [{label:"x",data:[1,2]}, {label:"y",data:[3,4]}]
//   ->  [{ name: "A", x: 1, y: 3 }, { name: "B", x: 2, y: 4 }]
function buildRows(payload) {
  const labels = Array.isArray(payload?.x_labels) ? payload.x_labels : [];
  const datasets = Array.isArray(payload?.datasets) ? payload.datasets : [];
  return labels.map((label, i) => {
    const row = { name: String(label) };
    datasets.forEach(ds => {
      const key = (ds.label || "value").toString();
      const v = Array.isArray(ds.data) ? ds.data[i] : null;
      row[key] = typeof v === "number" ? v : null;
    });
    return row;
  });
}

export default function ChatChart({ payload }) {
  const rows = useMemo(() => buildRows(payload), [payload]);
  const datasets = Array.isArray(payload?.datasets) ? payload.datasets : [];

  if (!rows.length || !datasets.length) {
    return null;
  }

  const isLine = payload?.type === "line";
  const ChartComponent = isLine ? LineChart : BarChart;
  const SeriesComponent = isLine ? Line : Bar;

  return (
    <div className="my-2 -mx-1 rounded-md border border-border bg-background/40 p-2">
      {payload?.title && (
        <p className="text-[11px] font-medium text-muted-foreground mb-1 px-1">
          {payload.title}
          {payload.currency ? ` (${payload.currency})` : ""}
        </p>
      )}
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer>
          <ChartComponent data={rows} margin={{ top: 5, right: 8, left: -16, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,120,120,0.2)" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={formatNumber} />
            <Tooltip
              contentStyle={{ fontSize: 11, padding: "4px 8px" }}
              formatter={(v) => (typeof v === "number" ? v.toLocaleString() : v)}
            />
            {datasets.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {datasets.map((ds, i) => {
              const key = (ds.label || `value_${i}`).toString();
              return isLine ? (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ) : (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COLORS[i % COLORS.length]}
                  radius={[3, 3, 0, 0]}
                />
              );
            })}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
